import * as SunCalc from "suncalc";

interface ClimateNormal {
    date: string;
    datatype: string;
    station: string;
    attributes: string;
    value: number;
}

interface NoaaResponse {
    metadata: {
        resultset: {
            offset: number;
            count: number;
            limit: number;
        };
    };
    results: ClimateNormal[];
}

interface Station {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
    elevation: number;
    mindate: string;
    maxdate: string;
    datacoverage: number;
}

interface StationsResponse {
    metadata: {
        resultset: {
            offset: number;
            count: number;
            limit: number;
        };
    };
    results: Station[];
}

interface MonthlyNormals {
    month: string;
    avgTemp?: number;
    maxTemp?: number;
    minTemp?: number;
    precipitation?: number;
    snowfall?: number;
    heatingDegreeDays?: number;
    coolingDegreeDays?: number;
}

const CORS_PROXY = 'https://corsproxy.io/?';

async function findNearestStation(
    lat: number,
    lng: number,
    radiusMiles: number = 25
): Promise<Station | null> {
    const apiKey = process.env.REACT_APP_NOAA_API_KEY;

    if( !apiKey ) {
        throw new Error( 'REACT_APP_NOAA_API_KEY not found' );
    }

    const url = CORS_PROXY + encodeURIComponent(
        `https://www.ncdc.noaa.gov/cdo-web/api/v2/stations?` +
        `datasetid=NORMAL_MLY&` +
        `extent=${lat - 0.5},${lng - 0.5},${lat + 0.5},${lng + 0.5}&` +
        `limit=100`
    );

    const response = await fetch( url, {
        headers: { 'token': apiKey }
    } );

    if( !response.ok ) {
        throw new Error( `NOAA API error: ${response.status}` );
    }

    const data: StationsResponse = await response.json();

    if( !data.results || data.results.length === 0 ) {
        return null;
    }

    let closest: Station | null = null;
    let minDistance = Infinity;

    data.results.forEach( station => {
        const distance = calculateDistance(
            lat, lng,
            station.latitude, station.longitude
        );

        if( distance < minDistance ) {
            minDistance = distance;
            closest = station;
        }
    } );

    return closest;
}

async function getNoaaClimateNormals(
    stationId: string
): Promise<ClimateNormal[]> {
    const apiKey = process.env.REACT_APP_NOAA_API_KEY;

    if( !apiKey ) {
        throw new Error( 'REACT_APP_NOAA_API_KEY not found' );
    }

    const url = CORS_PROXY + encodeURIComponent(
        `https://www.ncdc.noaa.gov/cdo-web/api/v2/data?` +
        `datasetid=NORMAL_MLY&` +
        `stationid=${stationId}&` +
        `startdate=2010-01-01&` +
        `enddate=2010-12-31&` +
        `units=standard&` +
        `limit=1000`
    );

    const response = await fetch( url, {
        headers: { 'token': apiKey }
    } );

    if( !response.ok ) {
        throw new Error( `NOAA API error: ${response.status}` );
    }

    const data: NoaaResponse = await response.json();
    return data.results;
}

function parseMonthlyNormals( normals: ClimateNormal[] ): MonthlyNormals[] {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const monthlyData: Map<number, Partial<MonthlyNormals>> = new Map();

    for( let i = 0; i < 12; i++ ) {
        monthlyData.set( i, { month: months[i] } );
    }

    normals.forEach( normal => {
        const date = new Date( normal.date );
        const monthIndex = date.getMonth();
        const monthData = monthlyData.get( monthIndex )!;

        switch( normal.datatype ) {
            case 'MLY-TAVG-NORMAL':
                monthData.avgTemp = normal.value;
                break;
            case 'MLY-TMAX-NORMAL':
                monthData.maxTemp = normal.value;
                break;
            case 'MLY-TMIN-NORMAL':
                monthData.minTemp = normal.value;
                break;
            case 'MLY-PRCP-NORMAL':
                monthData.precipitation = normal.value;
                break;
            case 'MLY-SNOW-NORMAL':
                monthData.snowfall = normal.value;
                break;
            case 'MLY-HTDD-NORMAL':
                monthData.heatingDegreeDays = normal.value;
                break;
            case 'MLY-CLDD-NORMAL':
                monthData.coolingDegreeDays = normal.value;
                break;
        }
    } );

    return Array.from( monthlyData.values() ) as MonthlyNormals[];
}

function calculateDistance(
    lat1: number, lon1: number,
    lat2: number, lon2: number
): number {
    const R = 3959; // Earth's radius in miles
    const dLat = toRad( lat2 - lat1 );
    const dLon = toRad( lon2 - lon1 );

    const a = Math.sin( dLat / 2 ) * Math.sin( dLat / 2 ) +
        Math.cos( toRad( lat1 ) ) * Math.cos( toRad( lat2 ) ) *
        Math.sin( dLon / 2 ) * Math.sin( dLon / 2 );

    const c = 2 * Math.atan2( Math.sqrt( a ), Math.sqrt( 1 - a ) );
    return R * c;
}

function toRad( degrees: number ): number {
    return degrees * ( Math.PI / 180 );
}

function calcMonthlyDaylight( lat: number, lng: number, monthIndex: number ): number {    
    const year = new Date().getFullYear();
    const daysInMonth = new Date( year, monthIndex + 1, 0 ).getDate();
    let totalHours = 0;
    for( let day = 1; day <= daysInMonth; day++ ) {
        const date = new Date( year, monthIndex, day );
        const daylight = getDaylightHours( lat, lng, date );
        totalHours += daylight;
    }
    return totalHours / daysInMonth; // Mean daily hours
}

function getDaylightHours( lat: number, lng: number, date: Date ): number {
    const times = SunCalc.getTimes( date, lat, lng );
    return ( times.sunset.getTime() - times.sunrise.getTime() ) / ( 1000 * 60 * 60 );
}

export {
    // findNearestStation,
    // getNoaaClimateNormals,
    // parseMonthlyNormals,
    // calculateDistance,
    calcMonthlyDaylight,
    // getDaylightHours,
    // type ClimateNormal,
    // type NoaaResponse,
    // type Station,
    // type StationsResponse,
    // type MonthlyNormals
};